import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Menu, X, ChevronDown, LogOut, PanelLeftClose } from 'lucide-react';
import { t } from '../utils/i18n';
import { useAuth } from '../context/AuthContext';
import { useDmPersona } from '../context/DmPersonaContext';
import { useApiEnvironment } from '../context/ApiEnvironmentContext';
import LanguageSwitcher from '../components/LanguageSwitcher/LanguageSwitcher';
import Logo from '../assets/images/logo.png';
import { API_ENV_PROD, API_ENV_TEST } from '../utils/apiEnvironment';
import Navigation from './Navigation';
import ThemeSwitcher from '../ui/ThemeSwitcher';
import { getPageContext } from '../ui/pageContexts';
import { getLocale } from '../utils/i18n';
import { getActiveGroup, getActiveItem } from './navigationConfig';
import { isAccountManager } from '../utils/adminPermissions';
import './Header.css';

export default function Header({ sidebarVisible, onToggleSidebar }) {
  const { user, logout, isAuthenticated } = useAuth();
  const { userId: personaUserId, username: personaUsername, isAuthenticated: hasPersona, logout: personaLogout } = useDmPersona();
  const { apiEnvironment, setApiEnvironment } = useApiEnvironment();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!userDropdownOpen) return undefined;
    const outside = (event) => {
      if (!userDropdownRef.current?.contains(event.target)) setUserDropdownOpen(false);
    };
    const escape = (event) => {
      if (event.key === 'Escape') {
        setUserDropdownOpen(false);
        userButtonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [userDropdownOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
    setUserDropdownOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const dialog = dialogRef.current;
    const trigger = menuButtonRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    closeButtonRef.current?.focus();
    document.body.style.overflow = 'hidden';
    const desktop = window.matchMedia('(min-width: 1280px)');
    const closeOnDesktop = () => { if (desktop.matches) setIsMenuOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      desktop.removeEventListener('change', closeOnDesktop);
      dialog.close();
      document.body.style.overflow = previousOverflow;
      trigger?.focus();
    };
  }, [isMenuOpen]);

  if (!isAuthenticated) return null;
  const initials = (user?.displayName || user?.email || 'U').slice(0, 2).toUpperCase();
  const isTest = apiEnvironment === API_ENV_TEST;
  const activeItem = getActiveItem(pathname, isTest, isAccountManager(user), search);
  const context = getPageContext(pathname, search);
  const pageTitle = context ? (context[getLocale()] || context.en).title : activeItem ? t(activeItem.key) : t('header.navigation.page');
  const group = getActiveGroup(pathname, isTest, isAccountManager(user), search);
  const nav = (idPrefix) => <Navigation key={`${pathname}-${search}-${isTest}`} idPrefix={idPrefix} activeItem={activeItem} isTest={isTest} onNavigate={() => setIsMenuOpen(false)} />;
  const containDrawerFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current.querySelectorAll('a[href], button, select, [tabindex="0"]')).filter((element) => element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  const brand = <Link to="/" className="admin-brand" onClick={() => setIsMenuOpen(false)}><img src={Logo} alt="" /><span>{t('common.brandName')}</span></Link>;

  return <>
    <a href="#admin-main" className="admin-skip-link">{t('header.navigation.skip')}</a>
    {sidebarVisible && <aside className="admin-sidebar" id="admin-desktop-navigation">
      <div className="admin-sidebar-brand">{brand}</div>
      {nav('desktop')}
    </aside>}
    <header className="admin-header">
      <button ref={menuButtonRef} type="button" className="admin-icon-button admin-mobile-menu" aria-label={t('header.navigation.show')} aria-expanded={isMenuOpen} aria-controls="admin-mobile-navigation" onClick={() => { setUserDropdownOpen(false); setIsMenuOpen(true); }}>
        <Menu size={22} aria-hidden="true" />
      </button>
      <button type="button" className="admin-icon-button admin-desktop-toggle" aria-label={t(sidebarVisible ? 'header.navigation.hide' : 'header.navigation.show')} aria-expanded={sidebarVisible} aria-controls="admin-desktop-navigation" onClick={onToggleSidebar}>
        {sidebarVisible ? <PanelLeftClose size={21} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
      </button>
      <div className="admin-page-location"><span className="admin-location-label">{group ? t(`header.navigation.${group.id}`) : t('common.brandName')}</span><span className="admin-page-title">{pageTitle}</span></div>
      <label className={`admin-environment ${isTest ? 'is-test' : 'is-prod'}`}>
        <span className="sr-only">{t('header.navigation.environment')}</span>
        <select aria-label={t('header.navigation.environment')} value={apiEnvironment} onChange={(event) => setApiEnvironment(event.target.value)}>
          <option value={API_ENV_TEST}>TEST</option><option value={API_ENV_PROD}>PROD</option>
        </select>
        <ChevronDown size={14} aria-hidden="true" />
      </label>
      <ThemeSwitcher />
      <div className="admin-account" ref={userDropdownRef}>
        <button ref={userButtonRef} type="button" className="admin-icon-button admin-avatar" aria-label={t('header.user.profile')} aria-expanded={userDropdownOpen} aria-controls="admin-account-panel" onClick={() => setUserDropdownOpen((value) => !value)}>{initials}</button>
        {userDropdownOpen && <div id="admin-account-panel" className="admin-account-panel">
          <div className="admin-account-section"><p className="admin-account-email">{user?.email}</p><p className="text-xs text-dark-text-secondary">{user?.role}</p><Link to="/profile" className="admin-account-action" onClick={() => setUserDropdownOpen(false)}>{t('header.user.profile')}</Link></div>
          <div className="admin-account-section"><p className="text-xs text-dark-text-secondary mb-2">{t('header.navigation.language')}</p><LanguageSwitcher /></div>
          {hasPersona && <div className="admin-account-section"><p className="text-xs text-dark-text-secondary">{t('header.user.dmPersona')}</p><p className="admin-account-email text-primary">{personaUsername} #{personaUserId}</p><button type="button" className="admin-account-action" onClick={() => { setUserDropdownOpen(false); personaLogout(); }}>{t('header.user.logoutPersona')}</button></div>}
          <button type="button" className="admin-account-action" onClick={() => { setUserDropdownOpen(false); personaLogout(); logout(); navigate('/login', { replace: true }); }}><LogOut size={16} aria-hidden="true" />{t('header.user.logout')}</button>
        </div>}
      </div>
    </header>
    {isMenuOpen && createPortal(<dialog ref={dialogRef} id="admin-mobile-navigation" className="admin-drawer" aria-label={t('header.navigation.label')} onKeyDown={containDrawerFocus} onCancel={() => setIsMenuOpen(false)} onClick={(event) => { if (event.target === event.currentTarget) setIsMenuOpen(false); }}>
      <div className="admin-drawer-inner">
        <div className="admin-drawer-heading">{brand}<button ref={closeButtonRef} type="button" className="admin-icon-button" aria-label={t('header.navigation.close')} onClick={() => setIsMenuOpen(false)}><X size={22} aria-hidden="true" /></button></div>
        <div className={`admin-drawer-environment ${isTest ? 'is-test' : 'is-prod'}`}>{t('header.navigation.environment')}<strong>{isTest ? 'TEST' : 'PROD'}</strong></div>
        {nav('mobile')}
      </div>
    </dialog>, document.body)}
  </>;
}
